import { createDirectus, rest } from '@directus/sdk';
import { z } from 'zod';

import type { Schema } from '../../../../types/DirectusSchema';
import { env } from '../../env.ts';
import type { DirectusError } from '../../types/directusError.ts';

// The promotor app has its own login, separate from the Directus Data Studio on the same host.
// The refresh token lives in an httpOnly cookie that only /promoter-auth-endpoint receives
// (see directus/extensions/gogigi/src/promoter-auth-endpoint/endpoints/session/session.ts), and
// the access token is kept in memory and sent as a Bearer header. Directus' own /auth/logout
// never sees that cookie, so logging out of the Data Studio leaves the promotor login alone.
//
// Data requests deliberately do not send cookies. Without that, a request made before the
// access token is known would fall back to whatever Data Studio session the browser holds.

const baseUrl = env.VITE_DIRECTUS_API_URL;
const sessionUrl = `${baseUrl}/promoter-auth-endpoint`;

/** Refresh this long before the access token expires. */
const REFRESH_MARGIN_MS = 30_000;

interface Session {
  accessToken: string;
  expiresAt: number;
}

const sessionBodySchema = z.object({
  data: z.object({ access_token: z.string(), expires: z.number() }),
});

const errorBodySchema = z.object({
  errors: z.array(z.object({ message: z.string(), extensions: z.looseObject({ code: z.string() }) })),
});

/** Same shape as SDK errors, which the login form already handles. */
class SessionError extends Error implements DirectusError {
  constructor(
    readonly errors: DirectusError['errors'],
    readonly response: Response,
  ) {
    super(errors.map((error) => error.message).join(', '));
    this.name = 'SessionError';
  }
}

let session: Session | null = null;
let pendingRefresh: Promise<Session | null> | null = null;
/** Set after a refresh found no login, so logged-out pages do not ask again on every request. */
let loggedOut = false;

const post = async (path: string, body?: unknown) =>
  fetch(`${sessionUrl}/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

const readJsonBody = async (response: Response): Promise<unknown> => {
  try {
    return (await response.json()) as unknown;
  } catch {
    return {};
  }
};

const readSession = async (response: Response): Promise<Session> => {
  const json = await readJsonBody(response);
  if (!response.ok) {
    const errors = errorBodySchema.safeParse(json).data?.errors;
    throw new SessionError(errors ?? [{ message: response.statusText, extensions: { code: 'UNKNOWN' } }], response);
  }
  const { data } = sessionBodySchema.parse(json);
  return { accessToken: data.access_token, expiresAt: Date.now() + data.expires };
};

const refreshSession = async (): Promise<Session | null> => {
  try {
    let response = await post('refresh');
    // Directus rejects a request carrying an expired Data Studio cookie and clears that cookie
    // in the same response, so one retry gets through.
    if (response.status === 401) response = await post('refresh');
    session = response.ok ? await readSession(response) : null;
  } catch {
    session = null;
  }
  loggedOut = session === null;
  return session;
};

export const auth = {
  async login(email: string, password: string) {
    session = await readSession(await post('login', { email, password }));
    loggedOut = false;
    return session;
  },

  /** Takes over a session the server started, e.g. right after email verification. */
  setSession(data: { access_token: string; expires: number }) {
    session = { accessToken: data.access_token, expiresAt: Date.now() + data.expires };
    loggedOut = false;
  },

  /** Restores or renews the session from the refresh cookie. Concurrent callers share one request. */
  async refresh() {
    pendingRefresh ??= (async () => {
      try {
        return await refreshSession();
      } finally {
        pendingRefresh = null;
      }
    })();
    return pendingRefresh;
  },

  async logout() {
    session = null;
    loggedOut = true;
    try {
      await post('logout');
    } catch {
      // The local session is gone either way; a failed request only leaves the refresh cookie behind.
    }
  },

  async getToken(): Promise<string | null> {
    if (pendingRefresh) await pendingRefresh;
    if (loggedOut) return null;
    if (!session || session.expiresAt - REFRESH_MARGIN_MS < Date.now()) await auth.refresh();
    return session?.accessToken ?? null;
  },
};

export const directusClient = createDirectus<Schema>(baseUrl)
  .with(() => ({ getToken: async () => auth.getToken() }))
  .with(rest());

/** Authorization header for requests made outside the SDK (fetch, XHR). */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await auth.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
